import React, { useState } from "react";

interface RequestQuoteModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const RequestQuoteModal: React.FC<RequestQuoteModalProps> = ({
    isOpen,
    onClose,
}) => {
    const [quoteData, setQuoteData] = useState({
        projectType: "",
        timeline: "",
        budget: "",
        description: "",
        name: "",
        email: "",
        phone: "",
        company: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<
        "idle" | "success" | "error"
    >("idle");

    if (!isOpen) return null;

    const handleInputChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
    ) => {
        const { name, value } = e.target;
        setQuoteData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus("idle");

        try {
            const formDataToSubmit = new FormData();
            
            // Map to API fields
            formDataToSubmit.append("name", quoteData.name);
            formDataToSubmit.append("email", quoteData.email);
            formDataToSubmit.append("phone", quoteData.phone);
            formDataToSubmit.append("organization", quoteData.company);
            formDataToSubmit.append("service", quoteData.projectType);
            formDataToSubmit.append("contactMethod", "email");
            formDataToSubmit.append("deadline", quoteData.timeline || new Date().toISOString().split('T')[0]);
            
            // Combine description, budget and timeline into details
            const detailedNotes = `
Budget: ${quoteData.budget}
Timeline: ${quoteData.timeline}
Description: ${quoteData.description}
            `.trim();
            formDataToSubmit.append("details", detailedNotes);

            const response = await fetch("https://partner.designsbydmi.com/api/contact-form", {
                method: "POST",
                body: formDataToSubmit,
            });

            if (response.ok) {
                setSubmitStatus("success");
                setQuoteData({
                    projectType: "",
                    timeline: "",
                    budget: "",
                    description: "",
                    name: "",
                    email: "",
                    phone: "",
                    company: "",
                });
                setTimeout(() => {
                    onClose();
                    setSubmitStatus("idle");
                }, 3000);
            } else {
                setSubmitStatus("error");
            }
        } catch (error) {
            setSubmitStatus("error");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-800 rounded-xl shadow-2xl border dark:border-slate-700">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 z-10"
                >
                    <i className="ri-close-line text-2xl"></i>
                </button>

                <div className="p-8 md:p-12">
                    <div className="text-center mb-10">
                        <div className="w-16 h-16 bg-[#e18033]/10 dark:bg-[#e18033]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                            <i className="ri-file-text-line text-2xl text-[#e18033]"></i>
                        </div>
                        <h2 className="text-2xl md:text-3xl font-bold text-[#212026] dark:text-white mb-4">
                            Request a Detailed Quote
                        </h2>
                        <p className="text-lg text-[#889d8f] dark:text-[#dbcfbf] max-w-2xl mx-auto">
                            Need a detailed estimate? Submit your project details and we'll
                            get back to you with a comprehensive quote within 48 hours.
                        </p>
                    </div>

                    <form
                        id="quote-request-modal"
                        data-dmi-form
                        onSubmit={handleSubmit}
                        className="space-y-6"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label
                                    htmlFor="name"
                                    className="block text-sm font-medium text-[#212026] dark:text-white mb-2"
                                >
                                    Full Name *
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={quoteData.name}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-4 py-3 border border-[#dbcfbf] dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-[#e18033] focus:border-transparent transition-colors text-sm text-[#212026] dark:text-white bg-white dark:bg-slate-700"
                                    placeholder="Enter your full name"
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="email"
                                    className="block text-sm font-medium text-[#212026] dark:text-white mb-2"
                                >
                                    Email Address *
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={quoteData.email}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-4 py-3 border border-[#dbcfbf] dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-[#e18033] focus:border-transparent transition-colors text-sm text-[#212026] dark:text-white bg-white dark:bg-slate-700"
                                    placeholder="Enter your email address"
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="phone"
                                    className="block text-sm font-medium text-[#212026] dark:text-white mb-2"
                                >
                                    Phone Number
                                </label>
                                <input
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    value={quoteData.phone}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 border border-[#dbcfbf] dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-[#e18033] focus:border-transparent transition-colors text-sm text-[#212026] dark:text-white bg-white dark:bg-slate-700"
                                    placeholder="Enter your phone number"
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="company"
                                    className="block text-sm font-medium text-[#212026] dark:text-white mb-2"
                                >
                                    Company/Organization
                                </label>
                                <input
                                    type="text"
                                    id="company"
                                    name="company"
                                    value={quoteData.company}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 border border-[#dbcfbf] dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-[#e18033] focus:border-transparent transition-colors text-sm text-[#212026] dark:text-white bg-white dark:bg-slate-700"
                                    placeholder="Enter company name"
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="projectType"
                                    className="block text-sm font-medium text-[#212026] dark:text-white mb-2"
                                >
                                    Project Type *
                                </label>
                                <div className="relative">
                                    <select
                                        id="projectType"
                                        name="projectType"
                                        value={quoteData.projectType}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-4 py-3 pr-8 border border-[#dbcfbf] dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-[#e18033] focus:border-transparent transition-colors text-sm appearance-none bg-white dark:bg-slate-700 text-[#212026] dark:text-white"
                                    >
                                        <option value="">Select project type</option>
                                        <option value="AutoCAD Drafting">AutoCAD Drafting</option>
                                        <option value="Revit 3D Modeling">Revit 3D Modeling</option>
                                        <option value="Dialux Lighting Design">
                                            Dialux Lighting Design
                                        </option>
                                        <option value="Floor Plans">Floor Plans</option>
                                        <option value="Line Diagrams">Line Diagrams</option>
                                        <option value="Multiple Services">Multiple Services</option>
                                        <option value="Custom Project">Custom Project</option>
                                    </select>
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                        <i className="ri-arrow-down-s-line text-[#889d8f] dark:text-[#dbcfbf]"></i>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label
                                    htmlFor="timeline"
                                    className="block text-sm font-medium text-[#212026] dark:text-white mb-2"
                                >
                                    Project Timeline
                                </label>
                                <div className="relative">
                                    <select
                                        id="timeline"
                                        name="timeline"
                                        value={quoteData.timeline}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 pr-8 border border-[#dbcfbf] dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-[#e18033] focus:border-transparent transition-colors text-sm appearance-none bg-white dark:bg-slate-700 text-[#212026] dark:text-white"
                                    >
                                        <option value="">Select timeline</option>
                                        <option value="Rush (1-3 days)">Rush (1-3 days)</option>
                                        <option value="Standard (1-2 weeks)">
                                            Standard (1-2 weeks)
                                        </option>
                                        <option value="Extended (2-4 weeks)">
                                            Extended (2-4 weeks)
                                        </option>
                                        <option value="Flexible">Flexible</option>
                                    </select>
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                        <i className="ri-arrow-down-s-line text-[#889d8f] dark:text-[#dbcfbf]"></i>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label
                                    htmlFor="budget"
                                    className="block text-sm font-medium text-[#212026] dark:text-white mb-2"
                                >
                                    Budget Range
                                </label>
                                <div className="relative">
                                    <select
                                        id="budget"
                                        name="budget"
                                        value={quoteData.budget}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 pr-8 border border-[#dbcfbf] dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-[#e18033] focus:border-transparent transition-colors text-sm appearance-none bg-white dark:bg-slate-700 text-[#212026] dark:text-white"
                                    >
                                        <option value="">Select budget range</option>
                                        <option value="Under $1,000">Under $1,000</option>
                                        <option value="$1,000 - $5,000">$1,000 - $5,000</option>
                                        <option value="$5,000 - $10,000">$5,000 - $10,000</option>
                                        <option value="$10,000 - $25,000">$10,000 - $25,000</option>
                                        <option value="Over $25,000">Over $25,000</option>
                                        <option value="To be discussed">To be discussed</option>
                                    </select>
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                        <i className="ri-arrow-down-s-line text-[#889d8f] dark:text-[#dbcfbf]"></i>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <label
                                htmlFor="description"
                                className="block text-sm font-medium text-[#212026] dark:text-white mb-2"
                            >
                                Project Description *
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                value={quoteData.description}
                                onChange={handleInputChange}
                                required
                                maxLength={500}
                                rows={6}
                                className="w-full px-4 py-3 border border-[#dbcfbf] dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-[#e18033] focus:border-transparent transition-colors text-sm resize-none text-[#212026] dark:text-white bg-white dark:bg-slate-700"
                                placeholder="Please provide detailed information about your project requirements, scope, deliverables, and any specific technical specifications..."
                            ></textarea>
                            <div className="text-right text-sm text-[#889d8f] dark:text-[#dbcfbf] mt-1">
                                {quoteData.description.length}/500 characters
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full bg-[#e18033] hover:bg-[#d16f29] text-white font-semibold py-4 px-8 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl"
                        >
                            {isSubmitting ? (
                                <span className="flex items-center justify-center">
                                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                                    Submitting Request...
                                </span>
                            ) : (
                                <span className="flex items-center justify-center">
                                    <i className="ri-file-text-line mr-2"></i>
                                    Request Detailed Quote
                                </span>
                            )}
                        </button>

                        {submitStatus === "success" && (
                            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                                <div className="flex items-center text-green-800 dark:text-green-200">
                                    <i className="ri-check-circle-line mr-2"></i>
                                    <span>
                                        Thank you! Your quote request has been submitted
                                        successfully. We'll send you a detailed proposal within 48
                                        hours.
                                    </span>
                                </div>
                            </div>
                        )}

                        {submitStatus === "error" && (
                            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                                <div className="flex items-center text-red-800 dark:text-red-200">
                                    <i className="ri-error-warning-line mr-2"></i>
                                    <span>
                                        Sorry, there was an error submitting your quote request.
                                        Please try again or contact us directly.
                                    </span>
                                </div>
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
};

export default RequestQuoteModal;
